import type { Writable } from 'ts-essentials';
import escapeStringRegexp from './utils';

export const getCookies = (): Record<string, string> => {
  const cookieObj = {} as Record<string, string>;

  const regex = /([a-zA-Z0-9_=:/]+)=([^;]+)/g;
  const matches = [...document.cookie.matchAll(regex)];

  matches.forEach((tokens) => {
    if (tokens?.length === 3) {
      const [, key, value] = tokens;
      cookieObj[key.toString().trim()] = value.toString().trim();
    }
  });
  return cookieObj;
};

export const getCookie = <T>(name: string, parseJson = false): null | T => {
  if (!name || typeof name !== 'string') return null;

  const value = document.cookie;

  const regex = new RegExp(`${escapeStringRegexp(name)}=([^;]+)`, 'g');
  const matches = [...value.matchAll(regex)];

  const values = [] as string[];
  matches.forEach((tokens) => {
    if (tokens?.length === 2) {
      const [, value] = tokens;
      values.push(value.toString().trim());
    }
  });

  const uniqueValues = [...new Set(values)];
  if (uniqueValues.length > 0) {
    if (parseJson) {
      try {
        return JSON.parse(uniqueValues[0]) as T;
      } catch (e) {
        return null;
      }
    }

    return uniqueValues[0] as unknown as T;
  }

  return null;
};

const defaultCookieOptions = Object.freeze({
  days: 365 as number,
  hours: 0 as number,
  path: '/' as string,
  domain: undefined as GetDomainType | undefined,
});

type CookieOptions = Writable<typeof defaultCookieOptions>;

export const setCookie = (name: string, value: string, options?: Partial<CookieOptions>) => {
  const opts = { ...defaultCookieOptions, ...options };

  const date = new Date();
  const ds = opts.days * 24 * 60 * 60 * 1000;
  const hs = opts.hours * 60 * 60 * 1000;
  date.setTime(date.getTime() + ds + hs);
  const expires = `expires=${date.toUTCString()}`;

  const domain = `domain=${getDomain(opts?.domain)}`;
  const path = `path=${opts.path}`;

  document.cookie = `${name}=${value}; ${expires}; ${domain}; ${path}`;
};

export const deleteCookie = (name: string) => {
  const date = new Date();
  date.setTime(date.getTime() - 1);
  const expires = `expires=${date.toUTCString()}`;
  const domain = `domain=${window.location.hostname}`;

  document.cookie = `${name}=; ${expires}; ${domain}`;
};

/**
 * subDomain: returns the full location hostname e.g. subdomain.zngly.com | test.subdomain.zngly.com
 * domain: returns the last two tokens of the location hostname e.g. zngly.com
 */
type GetDomainType = 'subDomain' | 'domain';

export const getDomain = (type: GetDomainType = 'subDomain') => {
  const domain = window.location.hostname;

  // if the domain starts with www. -> we can use only the last two tokens e.g. zngly.com
  if (domain.startsWith('www.')) {
    const tokens = domain.split('.');
    if (tokens.length === 3) return tokens.slice(1).join('.');
  }

  if (type === 'subDomain') return domain;

  const tokens = domain.split('.');
  if (!tokens.length) return domain;

  // get only the last two tokens
  return tokens.slice(-2).join('.');
};
